import {Component, Event, type EventEmitter, h, Prop, Element} from '@stencil/core';
import { makeLogger } from '../../../../../logger/logger';
import {GenericCardEventsLayer} from '../../../new-result-cards/generic-subcomponents/generic-card-event-layer/generic-card-event-layer';
import type {SearchProduct} from '../../../../../api/model/response/documents/public/Product';
import {ResultCardType} from '../../../new-result-cards/public/product-card/helpers/helpers';
import type {SearchResultClickEvent} from '../../SearchResultClickEvent';
import type {SearchApiDocument} from '../../../../../api/model/response/documents/SearchResult';
import type {Strategies} from '../../../../strategies/Strategies';
import EXCLAMATION_MARK_TRIANGLE from '../../../../../assets/svg/EXCLAMATION_MARK_TRIANGLE.svg';
import type {InternalAddressesStrategyService} from '../../../../strategies/InternalAddressesStrategyService';
import {BASE_URLS, INTERNAL_ADDRESSES} from '../../../../strategies/utils/variables';
import {formatDate} from '../../../result-cards/utils/DateFormatter';
import {EolDiscontinuedCardContainer} from './eol-discontinued-card-container';
import type { labelProviderInterface } from '../../../../LabelProviderInterface';
import type {BaseUrlsStrategyService} from '../../../../strategies/BasePathStrategyService';
import {getClickType} from '../../../../../utils/helpers/get-click-type';
import {getLocaleCode} from '../../../../../helpers/locale';

const { warn } = makeLogger({
  pathToCurrentFile: 'components/result-page/search-tab/subcomponents/eol-discontinued-result-list/uiaas-search-result-eol-discontinued.tsx',
  loggersList: ['warn']
});

@Component({
  tag: 'uiaas-search-result-eol-discontinued',
  styleUrl: 'uiaas-search-result-eol-discontinued.scss',
  shadow: true
})
export class ResultCardEolDiscontinued {

  @Prop({ mutable: true }) labelProvider?: labelProviderInterface;
  @Prop() readonly doc: SearchApiDocument;
  @Prop() readonly isDashboardView: boolean = false;
  @Prop() readonly index: number = null;
  @Prop() readonly strategies: Strategies;
  @Prop() readonly ignoreExternalIcon?: boolean = false;
  @Prop() readonly replacementsTotalHits: number = 0;
  @Prop() readonly rtl: boolean;
  @Prop() readonly isEolDocumentsGroup: boolean = false;
  @Prop() readonly term: string;
  @Prop() readonly language: string;
  @Prop() readonly country: string;

  componentWillLoad() {
      if (!this.labelProvider) {
        this.labelProvider = (name, fallback) => {
            warn('LabelProvider not provided in ResultLink component', [name, fallback]);
            return fallback;
        };
      }
  }

  @Event({
    eventName: 'search:ResultCardClick',
    composed: true,
    cancelable: true,
    bubbles: true
  })
    resultCardClickEventEmitter: EventEmitter<SearchResultClickEvent>;

  @Event({
    eventName: 'substitution-tool:ga-cta-banner-clicked',
    composed: true,
    cancelable: true,
    bubbles: true
  })
    ctaButtonClickEventEmitter: EventEmitter<{
      ctaLink: string,
      keyword: string,
      clickType: string,
    }>;

  @Element() el: HTMLElement;

  private mapDescriptionText = () => {
      if(this.isEolDocumentsGroup) return this.labelProvider('result.replaced-by-group', 'and is replaced by a group of products');
      switch(this.replacementsTotalHits) {
      case 0:
        return this.labelProvider('result.product.not-yet-replaced', 'and has not yet been replaced');
      case 1:
        return this.labelProvider('result.product.see-the-below-replacement', 'Please see the below replacement');
      default:
        return this.labelProvider('result.product.see-the-below-replacements', 'Please see the below replacements');
      }
  };

  private ctaButtonHandler = (e, productSubstitutionToolUrl) => {
      this.ctaButtonClickEventEmitter.emit({
        ctaLink: productSubstitutionToolUrl,
        keyword: this.term,
        clickType: getClickType(e),
      });
  };

  render() {
      const baseUrlsData: BaseUrlsStrategyService = this.strategies.for(BASE_URLS);
      const baseUrls = baseUrlsData.getBaseUrls();
      const substitutionToolUrl = baseUrls?.params?.productSubstitutionToolUrl;
      const internalAddressesStrategy: InternalAddressesStrategyService = this.strategies.for(INTERNAL_ADDRESSES);
      const endDate = this.doc.endOfCommercializationDate || this.doc.endOfServiceDate;
      const localeCode = getLocaleCode({language: this.language, country: this.country});
      const textWithDate = ` ${this.labelProvider('result.product.discontinued-on', 'has been discontinued on')} ${formatDate(endDate, localeCode)}`;
      const textWithoutDate = ` ${(this.labelProvider('result.product.has-been-discontunued', 'has been discontinued'))?.toLowerCase()}`;
      const replacementInfoText = this.mapDescriptionText();
      const extractedId = /<\/?[a-z][\s\S]*>/i.test(this.doc.id) ? this.doc.id.replace(/<\/?[^>]+(>|$)/g, '') : this.doc.id;
      const productSubstitutionToolUrl = `${substitutionToolUrl}${extractedId}`;
      return (
        <GenericCardEventsLayer clickEmitter={this.resultCardClickEventEmitter} resutCardType={ResultCardType.Basic}
          doc={this.doc as unknown as SearchProduct} index={this.index}>
          <EolDiscontinuedCardContainer doc={this.doc} strategies={this.strategies} rtl={this.rtl}>
            <section class={'eol-result-card__text-content-section'}>
              <div class="eol-result-card__title-content">
                <div
                  class="exclamation-mark-triangle-icon"
                  innerHTML={
                    EXCLAMATION_MARK_TRIANGLE
                  }
                ></div>
                <div class="eol-result-card__title">{`${extractedId} ${this.labelProvider('result.product.has-been-discontunued', 'has been Discontinued')}`}</div>
              </div>
              <qds-inline-link
                href={this.doc.url} target={internalAddressesStrategy.isExternalLink(this.doc.url) ? '_blank' : '_self'}
                tabindex='0'
                class="result-card__title-standalone-link eol-result-card__title-standalone-link-inline"
              >
                <span innerHTML={this.doc.title} class="eol-result-card__description" data-card-area={'banner'}>
                </span>

              </qds-inline-link>
              <span class='eol-result-card__subtitle'>
                <span>{`${endDate ? textWithDate : textWithoutDate}${this.replacementsTotalHits ? '. ' : ' '}`}</span>
                <span>{`${replacementInfoText}.`}</span>
              </span>
              {this.isEolDocumentsGroup
                ? <div class='eol-result-card__replacement-tool'>
                  <qds-button
                    text={this.labelProvider('result.product.view-all-replacements', 'View All Replacements')}
                    href={productSubstitutionToolUrl}
                    target="_blank"
                    onClick={(e) => this.ctaButtonHandler(e, productSubstitutionToolUrl)}
                    class="eol-result-card__replacement-tool-btn"/>
                </div>
                : null}
            </section>
          </EolDiscontinuedCardContainer>
        </GenericCardEventsLayer>
      );
  }
}
